import type { ClientGame, ClientGameCtx } from "./index";
import { AST } from "../../shared/arcade";
import { ArcadeControls } from "../controls";
import { sound } from "../sound";
import { Fx } from "../fx";
import { sticker, roundRectPath, OUTLINE } from "../draw";
import { C, TINT, displayFont, bodyFont, hudPill } from "../theme";

const MINE = C.green;
const RIVAL = C.coral;
const ROCK = TINT.grape;
const ROCK_DARK = "#B9A6F2";

export class AsteroidsClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private canvas!: HTMLCanvasElement;
  private controls = new ArcadeControls();
  private fx = new Fx();
  private raf = 0;
  private inputTimer = 0;
  private last = performance.now();
  private t = 0;
  private stars: { x: number; y: number; r: number; p: number }[] = [];
  private levelFlash = 0;

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();
    this.canvas = document.createElement("canvas");
    this.canvas.id = "game-canvas";
    ctx.root.appendChild(this.canvas);
    this.controls.mount(ctx.root, {
      actionLabel: "Fire", hint: "hold & drag to fly",
      radius: 78, deadzone: 0.3, curve: 2.2,
    });
    for (let i = 0; i < 70; i++) {
      this.stars.push({ x: Math.random(), y: Math.random(), r: 0.5 + Math.random() * 1.4, p: Math.random() * Math.PI * 2 });
    }

    ctx.room.onMessage("a-shoot", (msg: any) => {
      if (msg?.slot === ctx.mySlot) sound.laser();
    });
    ctx.room.onMessage("a-rock", (msg: any) => {
      sound.rockBoom(msg?.size ?? 2);
      this.fx.burst(msg?.x ?? 0, msg?.y ?? 0, { count: 12, speed: 0.09, colour: [ROCK, ROCK_DARK, C.white], life: 550, gravity: 0, drag: 0.998 });
    });
    ctx.room.onMessage("a-ship", (msg: any) => {
      sound.shipBoom();
      this.fx.burst(msg?.x ?? 0, msg?.y ?? 0, { count: 26, speed: 0.13, colour: msg?.slot === ctx.mySlot ? [MINE, TINT.mint] : [RIVAL, TINT.coral], life: 700, square: true, gravity: 0 });
      this.fx.shake(msg?.slot === ctx.mySlot ? 9 : 5, 380);
      try { navigator.vibrate?.(msg?.slot === ctx.mySlot ? [30, 40, 60] : 20); } catch { /* */ }
    });
    ctx.room.onMessage("a-level", () => { sound.levelUp(); this.levelFlash = 1; });

    this.inputTimer = window.setInterval(() => {
      if (this.ctx.room.state.phase !== "playing") return;
      this.ctx.room.send("input", {
        ax: this.controls.x, ay: this.controls.y, fire: this.controls.action,
      });
    }, 33);
    this.last = performance.now();
    this.loop();
  }

  unmount() {
    cancelAnimationFrame(this.raf);
    clearInterval(this.inputTimer);
    this.controls.unmount();
  }

  private loop = () => {
    const now = performance.now();
    const dt = Math.min(50, now - this.last);
    this.last = now;
    this.t += dt;
    try { this.draw(dt); } catch (e) { console.error("asteroids frame", e); }
    this.raf = requestAnimationFrame(this.loop);
  };

  private draw(dt: number) {
    const { room, mySlot } = this.ctx;
    const c = this.canvas;
    const g = c.getContext("2d")!;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const cw = c.clientWidth, ch = c.clientHeight;
    if (cw < 40 || ch < 40) return;
    if (c.width !== cw * dpr || c.height !== ch * dpr) { c.width = cw * dpr; c.height = ch * dpr; }
    g.save();
    g.scale(dpr, dpr);

    this.fx.update(dt);
    this.levelFlash = Math.max(0, this.levelFlash - dt * 0.002);
    const sh = this.fx.shakeXY();
    const st = room.state.asteroids;
    const me = st.ships?.[mySlot];
    const rival = st.ships?.[mySlot === 0 ? 1 : 0];

    // HUD first (layout only) so the playfield can sit just below it
    const ins = safeInsets();
    let hud: HudLayout;
    if (st.solo) {
      hud = hudLayout(g, cw, ins.top + 8, [
        { text: "♥".repeat(Math.max(0, me?.lives ?? 0)) || "–", ink: C.coral },
        { text: String(st.level), label: "level" },
        { text: String(me?.score ?? 0), label: "score" },
      ], room.state.mode === "solo");
    } else {
      const secs = Math.ceil((st.timeLeft ?? 0) / 1000);
      hud = hudLayout(g, cw, ins.top + 8, [
        { text: String(me?.score ?? 0), label: "you", fill: TINT.mint },
        { text: `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`, label: `first to ${AST.target2P}` },
        { text: String(rival?.score ?? 0), label: "rival", fill: TINT.coral },
      ], false);
    }

    // deep-night space, edge to edge
    g.fillStyle = C.nightDeep;
    g.fillRect(0, 0, cw, ch);

    const field = playfield(cw, ch, hud.bottom + 10, ins.bottom + controlsReserve(cw, ch), AST.W, AST.H);
    const { scale, ox, oy } = field;
    const bw = AST.W * scale, bh = AST.H * scale;

    // the board: a rounded night sticker with an ink outline + hard ink shadow
    boardSticker(g, ox, oy, bw, bh, C.night);

    // stars over everything (inside and outside the board)
    g.fillStyle = TINT.grape;
    for (const s of this.stars) {
      g.globalAlpha = 0.35 + 0.35 * Math.sin(this.t * 0.002 + s.p);
      g.beginPath();
      g.arc(s.x * cw, s.y * ch, s.r, 0, Math.PI * 2);
      g.fill();
    }
    g.globalAlpha = 1;

    g.save();
    roundRectPath(g, ox, oy, bw, bh, BOARD_R);
    g.clip();
    g.translate(ox + sh.x, oy + sh.y);
    g.scale(scale, scale);

    // rocks — pale grape sticker polygons with ink outlines
    st.rocks?.forEach((r: any, i: number) => {
      const rad = AST.sizes[r.size] ?? 16;
      g.save();
      g.translate(r.x, r.y);
      g.rotate((this.t * (r.spin || 0.0006)) % (Math.PI * 2));
      const path = () => {
        g.beginPath();
        for (let k = 0; k < 9; k++) {
          const a = (k / 9) * Math.PI * 2;
          const wob = 0.72 + 0.28 * Math.abs(Math.sin(i * 3.7 + k * 2.3));
          const px = Math.cos(a) * rad * wob;
          const py = Math.sin(a) * rad * wob;
          k === 0 ? g.moveTo(px, py) : g.lineTo(px, py);
        }
        g.closePath();
      };
      sticker(g, path, {
        fill: ROCK, outlineW: 2.2 / scale, shadowOffset: 3 / scale,
        shadowColour: "rgba(16,10,40,0.6)", highlight: C.white,
      });
      // a shaded facet on the far side from the light
      g.save();
      path();
      g.clip();
      g.fillStyle = "rgba(139,92,246,0.22)";
      g.beginPath();
      g.moveTo(rad, rad); g.lineTo(-rad * 0.2, rad); g.lineTo(rad, -rad * 0.2);
      g.closePath(); g.fill();
      g.restore();
      g.restore();
    });

    // bullets with a short trail
    st.bullets?.forEach((b: any) => {
      const col = b.owner === mySlot ? MINE : RIVAL;
      this.fx.trail(b.x, b.y, col, 1.8);
      g.fillStyle = col;
      g.strokeStyle = OUTLINE;
      g.lineWidth = 1.4 / scale;
      g.beginPath();
      g.arc(b.x, b.y, 2.8, 0, Math.PI * 2);
      g.fill();
      g.stroke();
    });

    // ships — filled sticker
    st.ships?.forEach((s: any, slot: number) => {
      if (!s.alive) return;
      if (s.inv > 0 && Math.floor(s.inv / 120) % 2 === 0) return;
      const col = slot === mySlot ? MINE : RIVAL;
      g.save();
      g.translate(s.x, s.y);
      g.rotate(s.angle);
      if (s.thrusting) {
        const flick = Math.random() * 0.6;
        sticker(g, () => {
          g.beginPath();
          g.moveTo(-AST.shipR * 0.5, AST.shipR * 0.38);
          g.lineTo(-AST.shipR * (1.4 + flick), 0);
          g.lineTo(-AST.shipR * 0.5, -AST.shipR * 0.38);
          g.closePath();
        }, { fill: C.mango, outlineW: 1.6 / scale, shadow: false });
      }
      const path = () => {
        g.beginPath();
        g.moveTo(AST.shipR + 3, 0);
        g.lineTo(-AST.shipR * 0.8, AST.shipR * 0.78);
        g.lineTo(-AST.shipR * 0.35, 0);
        g.lineTo(-AST.shipR * 0.8, -AST.shipR * 0.78);
        g.closePath();
      };
      sticker(g, path, {
        fill: col, outlineW: 2.4 / scale, shadowOffset: 2.5 / scale,
        shadowColour: "rgba(16,10,40,0.7)", highlight: "rgba(255,255,255,0.6)",
      });
      g.restore();
    });

    this.fx.draw(g);
    g.restore();

    if (this.levelFlash > 0) {
      g.fillStyle = `rgba(207,219,255,${0.35 * this.levelFlash})`;
      g.fillRect(0, 0, cw, ch);
    }
    hud.draw();
    if (room.state.phase === "countdown") countdownOverlay(g, cw, ch, room.state.countdown);
    g.restore();
  }

  results(ctx: ClientGameCtx) {
    const st = ctx.room.state.asteroids;
    const me = st.ships?.[ctx.mySlot];
    const rival = st.ships?.[ctx.mySlot === 0 ? 1 : 0];
    const d = document.createElement("div");
    d.className = "arc-final";
    d.innerHTML = st.solo
      ? `<div class="score-big">${me?.score ?? 0}</div><div class="arc-note">reached level ${st.level}</div>`
      : `<div class="score-big">${me?.score ?? 0} : ${rival?.score ?? 0}</div><div class="arc-note">asteroid score</div>`;
    return d;
  }
}

// ---------------------------------------------------------------------------
// Shared canvas chrome for the arcade games (Invaders, Boxing, Tug and others
// import these from here). Everything is screen space, CSS px.

/** corner radius of a game board sticker */
export const BOARD_R = 18;

/**
 * Countdown: a low-alpha ink dim with the number on a white sticker pill.
 * Works in any coordinate space (Hormuz calls it in world units) — sizes are
 * relative to the given width/height.
 */
export function countdownOverlay(
  g: CanvasRenderingContext2D, cw: number, ch: number, ms: number,
  opts: { go?: string; cy?: number } = {},
) {
  const left = ms ?? 0;
  const n = Math.ceil(left / 1000);
  const text = n > 0 ? String(n) : (opts.go ?? "Go!");
  g.save();
  g.fillStyle = "rgba(27,21,52,0.28)";
  g.fillRect(0, 0, cw, ch);
  // each number pops in slightly big, then settles
  const frac = (left % 1000) / 1000;
  const pop = 1 + 0.2 * Math.max(0, (frac - 0.8) / 0.2);
  const size = Math.max(40, Math.min(84, Math.min(cw, ch) * 0.17)) * pop;
  g.font = displayFont(Math.round(size));
  const tw = g.measureText(text).width;
  const h = size * 1.35;
  const w = Math.max(h, tw + size * 0.8);
  const cx = cw / 2, cy = opts.cy ?? ch / 2;
  const x = cx - w / 2, y = cy - h / 2;
  g.fillStyle = C.ink;
  roundRectPath(g, x + 4, y + 6, w, h, h / 2);
  g.fill();
  g.fillStyle = C.white;
  roundRectPath(g, x, y, w, h, h / 2);
  g.fill();
  g.lineWidth = 3;
  g.lineJoin = "round";
  g.strokeStyle = C.ink;
  g.stroke();
  g.fillStyle = n > 0 ? C.ink : C.green;
  g.textAlign = "center";
  g.textBaseline = "middle";
  if (n <= 0) {
    g.lineWidth = 5;
    g.strokeText(text, cx, cy + size * 0.06);
  }
  g.fillText(text, cx, cy + size * 0.06);
  g.restore();
}

let insetProbe: HTMLDivElement | null = null;
let insetKey = "";
let insetCache = { top: 0, bottom: 0 };
/** env(safe-area-inset-*) in CSS px, re-measured only when the viewport changes */
export function safeInsets(): { top: number; bottom: number } {
  const key = `${innerWidth}x${innerHeight}`;
  if (key === insetKey) return insetCache;
  try {
    if (!insetProbe || !insetProbe.isConnected) {
      insetProbe = document.createElement("div");
      insetProbe.style.cssText =
        "position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;" +
        "padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom)";
      document.body.appendChild(insetProbe);
    }
    const cs = getComputedStyle(insetProbe);
    insetCache = { top: parseFloat(cs.paddingTop) || 0, bottom: parseFloat(cs.paddingBottom) || 0 };
    insetKey = key;
  } catch { /* keep the last value */ }
  return insetCache;
}

/**
 * Bottom space to keep clear for the round action button (.punch-btn, 104px
 * at bottom 28px) on portrait screens. On landscape/desktop the playfield
 * gets the full height and the button floats over a corner, as before.
 */
export function controlsReserve(cw: number, ch: number): number {
  return ch > cw * 1.15 ? 140 : 12;
}

/**
 * Fit a W×H world as large as possible between `top` and `ch - bottom`, with
 * ≤12px side margins. Returns the single scale/offset used for drawing (and,
 * where a game maps touches into the world, for input too).
 */
export function playfield(cw: number, ch: number, top: number, bottom: number, W: number, H: number) {
  const availH = Math.max(40, ch - top - bottom);
  const scale = Math.max(0.05, Math.min((cw - 24) / W, availH / H));
  const ox = (cw - W * scale) / 2;
  const oy = top + (availH - H * scale) / 2;
  return { scale, ox, oy };
}

/** a rounded board: hard ink drop shadow, fill, 3px ink outline */
export function boardSticker(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, fill: string, r = BOARD_R) {
  g.save();
  g.fillStyle = C.ink;
  roundRectPath(g, x + 4, y + 6, w, h, r);
  g.fill();
  g.fillStyle = fill;
  roundRectPath(g, x, y, w, h, r);
  g.fill();
  g.lineWidth = 3;
  g.lineJoin = "round";
  // an ink edge vanishes against the night sky, so space boards get a pale rim
  g.strokeStyle = fill === C.night || fill === C.nightDeep ? TINT.grape : C.ink;
  g.stroke();
  g.restore();
}

export interface HudItem {
  text?: string;
  label?: string;
  fill?: string;
  ink?: string;
  /** custom pill content: its inner width and a painter (x = pill left, h = pill height) */
  w?: number;
  paint?: (g: CanvasRenderingContext2D, x: number, y: number, h: number) => void;
}
export interface HudLayout { bottom: number; draw: () => void }

const HUD_GAP = 8;
/** left edge clear of the quit button (36px at 7px) */
const HUD_LEFT = 52;

function pillWidth(g: CanvasRenderingContext2D, it: HudItem, size: number) {
  if (it.paint) return Math.round((it.w ?? 30) + size * 1.1);
  // mirrors hudPill()'s own sizing in theme.ts
  g.font = displayFont(size);
  const tw = g.measureText(it.text ?? "").width;
  let lw = 0;
  if (it.label) {
    g.font = bodyFont(Math.round(size * 0.62), 900);
    lw = g.measureText(it.label).width + size * 0.4;
  }
  return Math.round(tw + lw + size * 1.3);
}

/**
 * Lay out a row of white HUD sticker pills. Centred under the safe area by
 * default; with `avoidCentre` (solo runs, where the DOM "best score" banner
 * sits top-centre) they hug the top-right and wrap onto a second row rather
 * than reach the middle. Measure first, draw later: `bottom` tells the game
 * where its playfield can start.
 */
export function hudLayout(
  g: CanvasRenderingContext2D, cw: number, top: number,
  items: HudItem[], avoidCentre: boolean, size0 = 16,
): HudLayout {
  g.save();
  let size = size0;
  let ws = items.map((it) => pillWidth(g, it, size));
  const pos: { x: number; y: number }[] = [];
  let h = Math.round(size * 1.9);
  let y = top;
  if (!avoidCentre) {
    const avail = cw - HUD_LEFT * 2;
    let total = ws.reduce((a, b) => a + b, 0) + HUD_GAP * (items.length - 1);
    if (total > avail) {
      size = Math.max(11, Math.floor(size * avail / total));
      ws = items.map((it) => pillWidth(g, it, size));
      h = Math.round(size * 1.9);
      total = ws.reduce((a, b) => a + b, 0) + HUD_GAP * (items.length - 1);
    }
    let x = Math.max(HUD_LEFT, (cw - total) / 2);
    ws.forEach((w, i) => { pos[i] = { x, y }; x += w + HUD_GAP; });
  } else {
    const limit = Math.max(HUD_LEFT, cw / 2 + 84);
    let x = cw - 12;
    for (let i = items.length - 1; i >= 0; i--) {
      const w = ws[i]!;
      if (x - w < limit && x < cw - 12) { y += h + 8; x = cw - 12; }
      pos[i] = { x: x - w, y };
      x -= w + HUD_GAP;
    }
  }
  g.restore();
  const bottom = (items.length ? y + h + 3 : top);
  return {
    bottom,
    draw: () => {
      items.forEach((it, i) => {
        const p = pos[i]!;
        if (it.paint) {
          const w = ws[i]!;
          g.save();
          g.fillStyle = C.ink;
          roundRectPath(g, p.x, p.y + 3, w, h, h / 2);
          g.fill();
          g.fillStyle = it.fill ?? C.white;
          roundRectPath(g, p.x, p.y, w, h, h / 2);
          g.fill();
          g.lineWidth = 2.5;
          g.strokeStyle = C.ink;
          g.stroke();
          it.paint(g, p.x, p.y, h);
          g.restore();
        } else {
          hudPill(g, p.x, p.y, it.text ?? "", { size, label: it.label, fill: it.fill, ink: it.ink });
        }
      });
    },
  };
}
